import { FC, useContext } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  Textarea,
  Text,
  useToast,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { postRequest } from "repository/apiClient";
import { AuthContext } from "contexts/AuthProvider";
import { useCallback } from "react";
import useBID from "utils/useBid";
import { Review } from "../../../../../../api-schema/reviews";
import { ReviewCard } from "./ReviewCard";

type ReviewContactFormProps = {
  review: Review;
  onAfterSubmit: () => void;
};
const ReviewContactForm: FC<ReviewContactFormProps> = ({
  review,
  onAfterSubmit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      content: "",
    },
  });
  const { token } = useContext(AuthContext);
  const toast = useToast();
  const bId = useBID();
  const qId = review.qId;
  const onSubmit = useCallback(
    async (data: { content: string }) => {
      const { content } = data;

      try {
        await postRequest("contact", { content, bId, qId }, token);
        toast({
          title: "送信しました",
          description: "ご指摘ありがとうございます！",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setValue("content", "");
        onAfterSubmit();
      } catch {
        toast({
          title: "送信に失敗しました...",
          description: (
            <>
              再度お試しください。
              <br />
              連続して失敗する場合は恐れ入りますが、他の方法でお問い合わせください。
            </>
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [bId, onAfterSubmit, qId, setValue, toast, token]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <ReviewCard review={review} />
        <FormControl>
          <Textarea
            isInvalid={!!errors.content}
            {...register("content", {
              minLength: {
                value: 1,
                message: "内容を入力してください",
              },
              required: {
                value: true,
                message: "内容を入力してください",
              },
            })}
            placeholder="料理#味がマイナスの判定になっていますが、プラスの内容が書かれていると思います。"
          />
          <FormHelperText fontSize="small">
            お問い合わせに返信が必要な場合はその旨をお知らせください。
          </FormHelperText>
          {errors.content && (
            <FormHelperText fontSize="small">
              {errors.content.message}
            </FormHelperText>
          )}
        </FormControl>
        <Flex direction="row" alignItems="center" justifyContent="right">
          {isSubmitting && <Text>送信中です。</Text>}
          <Button
            colorScheme="teal"
            mr={3}
            disabled={isSubmitting}
            type="submit"
          >
            送信
          </Button>
        </Flex>
      </Stack>
    </form>
  );
};

export default ReviewContactForm;
